/**
 * Emblem — animated mark for "the Common".
 * Pure SVG + CSS animations: concentric rings, orbital nodes, central glow.
 * No images, no external assets.
 */
const Emblem = ({ size = 560, className = "" }) => {
  return (
    <div
      className={`relative ${className}`}
      style={{
        width: `min(${size}px, 92vw)`,
        height: `min(${size}px, 92vw)`,
        maxWidth: "100%",
      }}
      data-testid="emblem-mark"
      aria-hidden="true"
    >
      {/* Soft glow halo */}
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background:
            "radial-gradient(closest-side, rgba(214,178,94,0.18), rgba(88,210,199,0.06) 50%, transparent 70%)",
          filter: "blur(20px)",
        }}
      />

      {/* Outer dashed ring rotating slowly */}
      <svg
        viewBox="0 0 600 600"
        className="absolute inset-0 bc-rotate-slow"
        style={{ width: "100%", height: "100%" }}
      >
        <defs>
          <radialGradient id="bc-core" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#D6B25E" stopOpacity="1" />
            <stop offset="60%" stopColor="#D6B25E" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#D6B25E" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="bc-line" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#58D2C7" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#D6B25E" stopOpacity="0.55" />
          </linearGradient>
        </defs>

        <circle
          cx="300"
          cy="300"
          r="278"
          fill="none"
          stroke="rgba(246,247,251,0.12)"
          strokeWidth="1"
          strokeDasharray="2 6"
        />
        <circle
          cx="300"
          cy="300"
          r="240"
          fill="none"
          stroke="rgba(214,178,94,0.25)"
          strokeWidth="1"
        />
      </svg>

      {/* Mid ring with tick marks reverse rotation */}
      <svg
        viewBox="0 0 600 600"
        className="absolute inset-0 bc-rotate-mid"
        style={{ width: "100%", height: "100%" }}
      >
        <circle
          cx="300"
          cy="300"
          r="195"
          fill="none"
          stroke="rgba(88,210,199,0.28)"
          strokeWidth="1"
        />
        {Array.from({ length: 36 }).map((_, i) => {
          const a = (i * 360) / 36;
          return (
            <line
              key={i}
              x1="300"
              y1="105"
              x2="300"
              y2="118"
              stroke="rgba(246,247,251,0.18)"
              strokeWidth="1"
              transform={`rotate(${a} 300 300)`}
            />
          );
        })}
      </svg>

      {/* Inner orbit with constellation nodes */}
      <svg
        viewBox="0 0 600 600"
        className="absolute inset-0 bc-rotate-fast"
        style={{ width: "100%", height: "100%" }}
      >
        <circle
          cx="300"
          cy="300"
          r="140"
          fill="none"
          stroke="url(#bc-line)"
          strokeWidth="1"
        />
        {/* connection lines forming a weave */}
        {[
          [40, 220],
          [120, 300],
          [220, 60],
          [300, 180],
          [60, 320],
        ].map(([a, b], i) => {
          const r = 140;
          const ax = 300 + r * Math.cos((a * Math.PI) / 180);
          const ay = 300 + r * Math.sin((a * Math.PI) / 180);
          const bx = 300 + r * Math.cos((b * Math.PI) / 180);
          const by = 300 + r * Math.sin((b * Math.PI) / 180);
          return (
            <line
              key={i}
              x1={ax}
              y1={ay}
              x2={bx}
              y2={by}
              stroke="rgba(214,178,94,0.35)"
              strokeWidth="0.8"
            />
          );
        })}
        {/* nodes */}
        {[20, 75, 130, 195, 250, 305, 340].map((deg, i) => {
          const r = 140;
          const x = 300 + r * Math.cos((deg * Math.PI) / 180);
          const y = 300 + r * Math.sin((deg * Math.PI) / 180);
          return (
            <g key={i}>
              <circle cx={x} cy={y} r="6" fill="#0A0D14" stroke="#D6B25E" strokeWidth="1" />
              <circle cx={x} cy={y} r="2.4" fill="#D6B25E" />
            </g>
          );
        })}
      </svg>

      {/* Central radiating cross + core */}
      <svg
        viewBox="0 0 600 600"
        className="absolute inset-0"
        style={{ width: "100%", height: "100%" }}
      >
        {/* radiating rays */}
        {Array.from({ length: 8 }).map((_, i) => {
          const a = (i * 360) / 8;
          return (
            <line
              key={i}
              x1="300"
              y1="240"
              x2="300"
              y2="160"
              stroke="rgba(214,178,94,0.35)"
              strokeWidth="0.8"
              transform={`rotate(${a} 300 300)`}
            />
          );
        })}
        <circle cx="300" cy="300" r="62" fill="url(#bc-core)" className="bc-pulse" style={{ transformOrigin: "300px 300px" }} />
        <circle cx="300" cy="300" r="22" fill="#D6B25E" />
        <circle cx="300" cy="300" r="6" fill="#0A0D14" />
      </svg>

      {/* Floating teal pulse pip */}
      <div
        className="absolute bc-pulse"
        style={{
          top: "18%",
          left: "82%",
          width: 8,
          height: 8,
          background: "#58D2C7",
          boxShadow: "0 0 18px rgba(88,210,199,0.7)",
          borderRadius: "50%",
        }}
      />
      <div
        className="absolute bc-pulse"
        style={{
          top: "78%",
          left: "14%",
          width: 6,
          height: 6,
          background: "#1F7A5A",
          boxShadow: "0 0 14px rgba(31,122,90,0.75)",
          borderRadius: "50%",
          animationDelay: "1.4s",
        }}
      />
    </div>
  );
};

export default Emblem;
